import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CloseOutlined,
  DescriptionOutlined,
  EditOutlined,
  InfoOutlined,
  OpenInNewOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  TextField,
} from "@mui/material";

import { useAppFeedback } from "hooks/useAppFeedback";
import { useAuth } from "resources/AuthProvider";
import {
  deleteTurmaPesquisaSatisfacao,
  findTurmaCertificadoPesquisa,
  saveTurmaCertificado,
  saveTurmaPesquisaSatisfacao,
} from "API/apiTurmaCertificadoPesquisa";
import { findAllCertificateTemplates } from "API/apiCertificateTemplates";
import { findAllSatisfactionSurveyTemplates } from "API/apiSatisfactionSurveyTemplates";
import { CertificateTemplateDetail } from "types/CertificateTemplate";
import {
  SatisfactionSurveyQuestion,
  SatisfactionSurveyTemplate,
} from "types/SatisfactionSurveyTemplate";
import { TurmaAcaoInsert } from "types/acao";
import { formatarCpf, formatarDataDbParaBr } from "utils/Masks";

import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";
import styles from "./TurmaCertificadoPesquisaForm.module.css";

interface TurmaCertificadoPesquisaFormProps {
  onSaveSuccess?: () => void;
  turma?: TurmaAcaoInsert | null;
  acaoCurso?: string | null;
}

const certificateVariableLabels: Record<string, string> = {
  "{{nome}}": "Nome do participante",
  "{{cpf}}": "000.000.000-00",
  "{{email}}": "participante@email.com",
  "{{curso}}": "Nome da ação",
  "{{acao.curso}}": "Nome da ação",
  "{{turma}}": "Nome da turma",
  "{{cargaHoraria}}": "Carga horária",
  "{{dataConclusao}}": "Data de conclusão",
  "{{dataInicio}}": "Data de início",
  "{{dataFim}}": "Data de término",
  "{{instituicao}}": "Instituição responsável",
  "{{municipio}}": "Município",
  "{{codigoValidacao}}": "Código de validação",
  "{{codigo_certificado}}": "Código de validação",
  "{{dataEmissao}}": "Data de emissão",
  "{{data_emissao_certificado}}": "Data de emissão",
  "{{turma.modalidade}}": "Modalidade da turma",
  "{{turma.hora_inicio}}": "Horário inicial",
  "{{turma.hora_fim}}": "Horário final",
  "{{turma.endereco}}": "Endereço da turma",
  "{{instituicao_resp}}": "Instituição responsável",
  "{{acao.modalidade}}": "Modalidade da ação",
  "{{acao.tipologia}}": "Tipologia da ação",
  "{{acao.temas}}": "Temas da ação",
};

const satisfactionScaleOptions = [
  { value: 1, label: "Péssimo" },
  { value: 2, label: "Ruim" },
  { value: 3, label: "Regular" },
  { value: 4, label: "Bom" },
  { value: 5, label: "Ótimo" },
];

const getDateValue = (value?: string | null) => value?.slice(0, 10) || "";

const getTimeValue = (value?: string | null) => {
  const match = value?.match(/(\d{2}:\d{2})/);
  return match ? match[1] : "";
};

const getCertificateThumbnail = (template?: CertificateTemplateDetail | null) =>
  template?.pages.front.background?.dataUrl || "";

const replaceCertificateVariables = (
  value: string | null | undefined,
  variables: Record<string, string> = certificateVariableLabels,
) =>
  Object.entries(variables).reduce(
    (result, [variable, label]) => result.replaceAll(variable, label),
    value ?? "",
  );

const buildTurmaAddress = (turma?: TurmaAcaoInsert | null) => {
  if (!turma) return "Não informado";

  const street = [turma.logradouro, turma.numero].filter(Boolean).join(", ");
  const city = [turma.cidade, turma.uf].filter(Boolean).join(" - ");
  const address = [street, turma.complemento, turma.bairro, city, turma.cep ? `CEP ${turma.cep}` : ""]
    .filter(Boolean)
    .join(" | ");

  return address || "Não informado";
};

const buildCertificatePreviewVariables = (
  turma: TurmaAcaoInsert | null | undefined,
  user: { nome?: string; nome_social?: string; cpf?: string; email?: string } | null,
  acaoCurso?: string | null,
): Record<string, string> => {
  const notInformed = "Não informado";
  const startDate = formatarDataDbParaBr(turma?.data_inicio) || notInformed;
  const endDate = formatarDataDbParaBr(turma?.data_fim) || notInformed;
  const modalidade = turma?.modalidade_id ? `Modalidade ${turma.modalidade_id}` : notInformed;
  const courseName = acaoCurso || turma?.acao?.curso || turma?.acao?.nome || notInformed;
  const emptyVariables = Object.fromEntries(
    Object.keys(certificateVariableLabels).map((variable) => [variable, notInformed]),
  ) as Record<string, string>;

  return {
    ...emptyVariables,
    "{{nome}}": user?.nome_social || user?.nome || notInformed,
    "{{cpf}}": formatarCpf(user?.cpf) || notInformed,
    "{{email}}": user?.email || notInformed,
    "{{curso}}": courseName,
    "{{acao.curso}}": courseName,
    "{{turma}}": turma?.nome || notInformed,
    "{{cargaHoraria}}": turma?.carga_horaria ? `${turma.carga_horaria} horas` : notInformed,
    "{{dataConclusao}}": endDate,
    "{{dataInicio}}": startDate,
    "{{dataFim}}": endDate,
    "{{municipio}}": turma?.cidade || notInformed,
    "{{turma.modalidade}}": modalidade,
    "{{turma.hora_inicio}}": turma?.hora_inicio || notInformed,
    "{{turma.hora_fim}}": turma?.hora_fim || notInformed,
    "{{turma.endereco}}": buildTurmaAddress(turma),
    "{{turma.cep}}": turma?.cep || notInformed,
    "{{turma.logradouro}}": turma?.logradouro || notInformed,
    "{{turma.numero}}": turma?.numero || notInformed,
    "{{turma.complemento}}": turma?.complemento || notInformed,
    "{{turma.bairro}}": turma?.bairro || notInformed,
    "{{turma.cidade}}": turma?.cidade || notInformed,
    "{{turma.uf}}": turma?.uf || notInformed,
    "{{acao.modalidade}}": modalidade,
  };
};

const TurmaCertificadoPesquisaForm = ({
  onSaveSuccess,
  turma,
  acaoCurso,
}: TurmaCertificadoPesquisaFormProps) => {
  const [searchParams] = useSearchParams();
  const turmaId = searchParams.get("id");
  const { showError, showSuccess } = useAppFeedback();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [certificateTemplates, setCertificateTemplates] = useState<
    CertificateTemplateDetail[]
  >([]);
  const [surveyTemplates, setSurveyTemplates] = useState<
    SatisfactionSurveyTemplate[]
  >([]);
  const [certificateTemplateId, setCertificateTemplateId] = useState<
    number | null
  >(null);
  const [certificateStartDate, setCertificateStartDate] = useState("");
  const [certificateStartTime, setCertificateStartTime] = useState("");
  const [certificateEndDate, setCertificateEndDate] = useState("");
  const [certificateEndTime, setCertificateEndTime] = useState("");
  const [surveyTemplateId, setSurveyTemplateId] = useState<number | null>(null);
  const [surveyRequiredForCertificate, setSurveyRequiredForCertificate] =
    useState(false);
  const [hasExistingSurveyConfig, setHasExistingSurveyConfig] = useState(false);
  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false);
  const [previewCertificate, setPreviewCertificate] =
    useState<CertificateTemplateDetail | null>(null);
  const [previewSurvey, setPreviewSurvey] =
    useState<SatisfactionSurveyTemplate | null>(null);
  const [templateSearch, setTemplateSearch] = useState("");
  const [draftCertificateId, setDraftCertificateId] = useState<number | null>(
    null,
  );
  const [draftSurveyId, setDraftSurveyId] = useState<number | null>(null);

  const selectedCertificate = useMemo(
    () =>
      certificateTemplates.find(
        (template) => template.id === certificateTemplateId,
      ) || null,
    [certificateTemplateId, certificateTemplates],
  );
  const selectedSurvey = useMemo(
    () =>
      surveyTemplates.find((template) => template.id === surveyTemplateId) ||
      null,
    [surveyTemplateId, surveyTemplates],
  );
  const certificatePreviewVariables = useMemo(
    () => buildCertificatePreviewVariables(turma, user, acaoCurso),
    [acaoCurso, turma, user],
  );
  const filteredCertificates = useMemo(() => {
    const term = templateSearch.trim().toLocaleLowerCase("pt-BR");
    return term
      ? certificateTemplates.filter((template) =>
          template.name.toLocaleLowerCase("pt-BR").includes(term),
        )
      : certificateTemplates;
  }, [certificateTemplates, templateSearch]);
  const filteredSurveys = useMemo(() => {
    const term = templateSearch.trim().toLocaleLowerCase("pt-BR");
    return term
      ? surveyTemplates.filter((template) =>
          template.titulo.toLocaleLowerCase("pt-BR").includes(term),
        )
      : surveyTemplates;
  }, [surveyTemplates, templateSearch]);

  const loadConfiguration = async () => {
    if (!turmaId) return;
    const configuration = await findTurmaCertificadoPesquisa(turmaId);
    const certificate = configuration.certificado;
    const survey = configuration.pesquisa_satisfacao;

    setCertificateTemplateId(certificate?.certificate_template_id ?? null);
    setCertificateStartDate(
      getDateValue(certificate?.data_inicio_disponibilizacao),
    );
    setCertificateStartTime(
      getTimeValue(certificate?.hora_inicio_disponibilizacao) || "00:00",
    );
    setCertificateEndDate(getDateValue(certificate?.data_fim_disponibilizacao));
    setCertificateEndTime(getTimeValue(certificate?.hora_fim_disponibilizacao));
    setSurveyTemplateId(survey?.satisfaction_survey_template_id ?? null);
    setSurveyRequiredForCertificate(
      survey?.obrigatoria_para_certificado ?? false,
    );
    setHasExistingSurveyConfig(Boolean(survey));
  };

  useEffect(() => {
    if (!turmaId) {
      showError("Não foi possível encontrar o código da turma.");
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const [certificateResponse, surveyResponse] = await Promise.all([
          findAllCertificateTemplates(1, 999),
          findAllSatisfactionSurveyTemplates(1, 999),
          loadConfiguration(),
        ]);
        setCertificateTemplates(
          certificateResponse.certificate_templates.filter(
            (template) => template.active,
          ),
        );
        setSurveyTemplates(
          surveyResponse.satisfaction_survey_templates.filter(
            (template) => template.active,
          ),
        );
      } catch (error: any) {
        showError(
          error?.message ||
            "Erro ao carregar configurações de certificado e pesquisa.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [turmaId, showError]);

  const openCertificateDialog = () => {
    setTemplateSearch("");
    setDraftCertificateId(certificateTemplateId);
    setCertificateDialogOpen(true);
  };

  const openSurveyDialog = () => {
    setTemplateSearch("");
    setDraftSurveyId(surveyTemplateId);
    setSurveyDialogOpen(true);
  };

  const validateForm = () => {
    if (!certificateTemplateId) {
      showError("Selecione um modelo de certificado.");
      return false;
    }
    if (!certificateStartDate || !certificateStartTime) {
      showError(
        "Preencha a data e o horário inicial de disponibilização do certificado.",
      );
      return false;
    }
    if (
      (certificateEndDate && !certificateEndTime) ||
      (!certificateEndDate && certificateEndTime)
    ) {
      showError(
        "Preencha a data e o horário final ou deixe ambos os campos vazios.",
      );
      return false;
    }

    const start = new Date(`${certificateStartDate}T${certificateStartTime}`);
    const end =
      certificateEndDate && certificateEndTime
        ? new Date(`${certificateEndDate}T${certificateEndTime}`)
        : null;
    if (end && end.getTime() < start.getTime()) {
      showError(
        "A data e o horário final devem ser iguais ou posteriores ao início.",
      );
      return false;
    }
    return true;
  };

  const saveConfigurations = async () => {
    if (!turmaId || !validateForm()) return;
    try {
      setLoadingSave(true);
      await saveTurmaCertificado(turmaId, {
        certificate_template_id: certificateTemplateId as number,
        data_inicio_disponibilizacao: certificateStartDate,
        hora_inicio_disponibilizacao: certificateStartTime,
        data_fim_disponibilizacao: certificateEndDate || null,
        hora_fim_disponibilizacao: certificateEndTime || null,
      });
      if (surveyTemplateId) {
        await saveTurmaPesquisaSatisfacao(turmaId, {
          satisfaction_survey_template_id: surveyTemplateId,
          obrigatoria_para_certificado: surveyRequiredForCertificate,
        });
      } else if (hasExistingSurveyConfig) {
        await deleteTurmaPesquisaSatisfacao(turmaId);
      }
      await loadConfiguration();
      showSuccess(
        "Configurações de certificado e pesquisa salvas com sucesso.",
      );
      onSaveSuccess?.();
    } catch (error: any) {
      showError(
        error?.message ||
          "Erro ao salvar configurações de certificado e pesquisa.",
      );
    } finally {
      setLoadingSave(false);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className={styles.container}>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Configuração do certificado</h2>
        <p className={styles.sectionDescription}>
          Selecione o modelo de certificado e defina o período e horário em que
          estará disponível para os participantes.
        </p>
        <label className={stylesInput.titleInput}>
          Modelo de certificado *
        </label>
        <SelectedCertificate
          template={selectedCertificate}
          onSelect={openCertificateDialog}
          onPreview={setPreviewCertificate}
        />
        <div className={styles.availabilityGrid}>
          <Field
            label="Data inicial de disponibilização *"
            type="date"
            value={certificateStartDate}
            onChange={setCertificateStartDate}
          />
          <Field
            label="Horário inicial *"
            type="time"
            value={certificateStartTime}
            onChange={setCertificateStartTime}
          />
          <Field
            label="Data final de disponibilização (opcional)"
            type="date"
            value={certificateEndDate}
            onChange={setCertificateEndDate}
          />
          <Field
            label="Horário final (opcional)"
            type="time"
            value={certificateEndTime}
            onChange={setCertificateEndTime}
          />
        </div>
        <div className={styles.availabilityInfo}>
          <InfoOutlined fontSize="small" /> O certificado ficará disponível para
          emissão pelos participantes nesse período e horário.
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          Configuração da pesquisa de satisfação
        </h2>
        <p className={styles.sectionDescription}>
          Selecione o modelo de pesquisa que será aplicada aos participantes
          desta turma.
        </p>
        <label className={stylesInput.titleInput}>Modelo de pesquisa</label>
        <SelectedSurvey
          template={selectedSurvey}
          onSelect={openSurveyDialog}
          onPreview={setPreviewSurvey}
        />
        <div className={styles.checkboxBlock}>
          <FormControlLabel
            control={
              <Checkbox
                checked={surveyRequiredForCertificate}
                disabled={!surveyTemplateId}
                onChange={(event) =>
                  setSurveyRequiredForCertificate(event.target.checked)
                }
              />
            }
            label="Exigir preenchimento da pesquisa para emissão do certificado"
          />
          <p className={styles.checkboxHelp}>
            O participante só poderá emitir o certificado após concluir a
            pesquisa de satisfação.
          </p>
        </div>
      </section>

      <div className={styles.footer}>
        <Button
          className={`${stylesButton.buttonSoftPrimary} ${loadingSave ? stylesButton.buttonLoad : ""}`}
          disabled={loadingSave}
          onClick={saveConfigurations}
        >
          {loadingSave ? (
            <>
              <CircularProgress size={16} color="inherit" />
              <span>Salvando...</span>
            </>
          ) : (
            "Salvar configurações"
          )}
        </Button>
      </div>

      <TemplateSelectionDialog
        open={certificateDialogOpen}
        title="Selecionar modelo de certificado"
        description="Escolha o modelo que será utilizado para gerar os certificados desta turma."
        searchValue={templateSearch}
        onSearchChange={setTemplateSearch}
        onClose={() => setCertificateDialogOpen(false)}
        onConfirm={() => {
          setCertificateTemplateId(draftCertificateId);
          setCertificateDialogOpen(false);
        }}
        total={filteredCertificates.length}
      >
        {filteredCertificates.map((template) => (
          <CertificateTemplateCard
            key={template.id}
            template={template}
            selected={draftCertificateId === template.id}
            currentlySelected={certificateTemplateId === template.id}
            onSelect={() => setDraftCertificateId(template.id)}
            onPreview={() => setPreviewCertificate(template)}
          />
        ))}
      </TemplateSelectionDialog>
      <TemplateSelectionDialog
        open={surveyDialogOpen}
        title="Selecionar modelo de pesquisa de satisfação"
        description="Escolha o modelo que será aplicado aos participantes desta turma."
        searchValue={templateSearch}
        onSearchChange={setTemplateSearch}
        onClose={() => setSurveyDialogOpen(false)}
        onConfirm={() => {
          setSurveyTemplateId(draftSurveyId);
          if (!draftSurveyId) setSurveyRequiredForCertificate(false);
          setSurveyDialogOpen(false);
        }}
        total={filteredSurveys.length}
      >
        {filteredSurveys.map((template) => (
          <SurveyTemplateCard
            key={template.id}
            template={template}
            selected={draftSurveyId === template.id}
            currentlySelected={surveyTemplateId === template.id}
            onSelect={() => setDraftSurveyId(template.id)}
            onPreview={() => setPreviewSurvey(template)}
          />
        ))}
      </TemplateSelectionDialog>
      <CertificatePreviewDialog
        template={previewCertificate}
        variables={certificatePreviewVariables}
        onClose={() => setPreviewCertificate(null)}
      />
      <SurveyPreviewDialog
        template={previewSurvey}
        onClose={() => setPreviewSurvey(null)}
      />
    </div>
  );
};

const Field = ({
  label,
  type,
  value,
  onChange,
}: {
  label: string;
  type: "date" | "time";
  value: string;
  onChange: (value: string) => void;
}) => (
  <div>
    <label className={stylesInput.titleInput}>{label}</label>
    <TextField
      type={type}
      size="small"
      fullWidth
      value={value}
      onChange={(event) => onChange(event.target.value)}
      InputProps={{ className: stylesInput.inputSoft }}
    />
  </div>
);

const SelectedCertificate = ({
  template,
  onSelect,
  onPreview,
}: {
  template: CertificateTemplateDetail | null;
  onSelect: () => void;
  onPreview: (template: CertificateTemplateDetail) => void;
}) => {
  if (!template)
    return (
      <EmptySelector
        description="Escolha o modelo que será utilizado para gerar os certificados."
        onSelect={onSelect}
      />
    );
  const thumbnail = getCertificateThumbnail(template);
  return (
    <div className={styles.selector}>
      <div className={styles.certificateThumbnail}>
        {thumbnail ? (
          <img src={thumbnail} alt={`Miniatura de ${template.name}`} />
        ) : (
          <DescriptionOutlined fontSize="large" />
        )}
      </div>
      <div className={styles.selectorInfo}>
        <div className={styles.selectorTitleRow}>
          <p className={styles.selectorTitle}>{template.name}</p>
          <span className={styles.statusActive}>Ativo</span>
        </div>
        <p className={styles.selectorText}>
          {template.documentTypeLabel} com {template.variables.length} campos
          configurados.
        </p>
        <button
          type="button"
          className={styles.previewLink}
          onClick={() => onPreview(template)}
        >
          Visualizar modelo <OpenInNewOutlined fontSize="small" />
        </button>
      </div>
      <Button
        className={`${stylesButton.buttonSoftOutline} ${styles.selectorAction}`}
        onClick={onSelect}
        startIcon={<EditOutlined />}
      >
        Alterar modelo
      </Button>
    </div>
  );
};

const SelectedSurvey = ({
  template,
  onSelect,
  onPreview,
}: {
  template: SatisfactionSurveyTemplate | null;
  onSelect: () => void;
  onPreview: (template: SatisfactionSurveyTemplate) => void;
}) => {
  if (!template)
    return (
      <EmptySelector
        description="Escolha a pesquisa que será aplicada aos participantes desta turma."
        onSelect={onSelect}
      />
    );
  return (
    <div className={styles.selector}>
      <div className={styles.surveyIcon}>
        <DescriptionOutlined fontSize="large" />
      </div>
      <div className={styles.selectorInfo}>
        <div className={styles.selectorTitleRow}>
          <p className={styles.selectorTitle}>{template.titulo}</p>
          <span className={styles.statusActive}>Ativo</span>
        </div>
        <p className={styles.selectorText}>
          {template.questions.length} perguntas
        </p>
        <button
          type="button"
          className={styles.previewLink}
          onClick={() => onPreview(template)}
        >
          Visualizar pesquisa <OpenInNewOutlined fontSize="small" />
        </button>
      </div>
      <Button
        className={`${stylesButton.buttonSoftOutline} ${styles.selectorAction}`}
        onClick={onSelect}
        startIcon={<EditOutlined />}
      >
        Alterar modelo
      </Button>
    </div>
  );
};

const EmptySelector = ({
  description,
  onSelect,
}: {
  description: string;
  onSelect: () => void;
}) => (
  <div className={`${styles.selector} ${styles.selectorEmpty}`}>
    <div className={styles.selectorInfo}>
      <p className={styles.selectorTitle}>Nenhum modelo selecionado</p>
      <p className={styles.selectorText}>{description}</p>
    </div>
    <Button
      className={stylesButton.buttonSoftOutline}
      onClick={onSelect}
      startIcon={<EditOutlined />}
    >
      Selecionar modelo
    </Button>
  </div>
);

interface TemplateSelectionDialogProps {
  open: boolean;
  title: string;
  description: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  total: number;
  children: React.ReactNode;
}
const TemplateSelectionDialog = ({
  open,
  title,
  description,
  searchValue,
  onSearchChange,
  onClose,
  onConfirm,
  total,
  children,
}: TemplateSelectionDialogProps) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <div className={styles.dialogTitle}>
      <div>
        <h3 className={styles.dialogHeading}>{title}</h3>
        <p className={styles.dialogDescription}>{description}</p>
      </div>
      <IconButton onClick={onClose} aria-label="Fechar">
        <CloseOutlined />
      </IconButton>
    </div>
    <div className={styles.dialogContent}>
      <TextField
        className={styles.searchField}
        placeholder="Buscar modelo..."
        size="small"
        fullWidth
        value={searchValue}
        onChange={(event) => onSearchChange(event.target.value)}
        InputProps={{
          startAdornment: <SearchOutlined fontSize="small" />,
          className: stylesInput.inputSoft,
        }}
      />
      <div className={styles.templateGrid}>{children}</div>
    </div>
    <div className={styles.dialogFooter}>
      <span className={styles.selectorText}>{total} modelos disponíveis</span>
      <div>
        <Button className={stylesButton.buttonSoftOutline} onClick={onClose}>
          Cancelar
        </Button>
        <Button className={stylesButton.buttonSoftPrimary} onClick={onConfirm}>
          Usar modelo selecionado
        </Button>
      </div>
    </div>
  </Dialog>
);

const CertificateTemplateCard = ({
  template,
  selected,
  currentlySelected,
  onSelect,
  onPreview,
}: {
  template: CertificateTemplateDetail;
  selected: boolean;
  currentlySelected: boolean;
  onSelect: () => void;
  onPreview: () => void;
}) => {
  const thumbnail = getCertificateThumbnail(template);
  return (
    <article
      className={`${styles.templateCard} ${selected ? styles.templateCardSelected : ""}`}
    >
      <div className={styles.templateCardTop}>
        <div className={styles.templateThumbnail}>
          {thumbnail ? (
            <img src={thumbnail} alt={`Miniatura de ${template.name}`} />
          ) : (
            <DescriptionOutlined fontSize="large" />
          )}
        </div>
        <div className={styles.templateDetails}>
          <p className={styles.templateName}>{template.name}</p>
          <span className={styles.statusActive}>Ativo</span>
          <p className={styles.templateDescription}>
            {template.documentTypeLabel} com {template.variables.length} campos
            configurados.
          </p>
          <button
            type="button"
            className={styles.previewLink}
            onClick={onPreview}
          >
            Visualizar modelo <OpenInNewOutlined fontSize="small" />
          </button>
        </div>
      </div>
      <label className={styles.selectionControl}>
        <Radio checked={selected} onChange={onSelect} />
        {currentlySelected
          ? "Modelo atualmente selecionado"
          : "Selecionar este modelo"}
      </label>
    </article>
  );
};

const SurveyTemplateCard = ({
  template,
  selected,
  currentlySelected,
  onSelect,
  onPreview,
}: {
  template: SatisfactionSurveyTemplate;
  selected: boolean;
  currentlySelected: boolean;
  onSelect: () => void;
  onPreview: () => void;
}) => (
  <article
    className={`${styles.templateCard} ${selected ? styles.templateCardSelected : ""}`}
  >
    <div className={styles.templateCardTop}>
      <div className={styles.surveyIcon}>
        <DescriptionOutlined fontSize="large" />
      </div>
      <div className={styles.templateDetails}>
        <p className={styles.templateName}>{template.titulo}</p>
        <span className={styles.statusActive}>Ativo</span>
        <p className={styles.templateDescription}>
          {template.questions.length} perguntas
          {template.descricao ? ` • ${template.descricao}` : ""}
        </p>
        <button
          type="button"
          className={styles.previewLink}
          onClick={onPreview}
        >
          Visualizar pesquisa <OpenInNewOutlined fontSize="small" />
        </button>
      </div>
    </div>
    <label className={styles.selectionControl}>
      <Radio checked={selected} onChange={onSelect} />
      {currentlySelected
        ? "Modelo atualmente selecionado"
        : "Selecionar este modelo"}
    </label>
  </article>
);

const CertificatePreviewDialog = ({
  template,
  variables,
  onClose,
}: {
  template: CertificateTemplateDetail | null;
  variables: Record<string, string>;
  onClose: () => void;
}) => {
  if (!template) return null;
  const page = template.pages.front;
  const backgroundImage = page.background?.dataUrl
    ? `url(${page.background.dataUrl})`
    : "none";
  const isLandscape = template.page.orientation === "landscape";
  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(1024px, calc(100vw - 32px))",
          maxWidth: "1024px",
        },
      }}
    >
      <div className={styles.dialogTitle}>
        <div>
          <h3 className={styles.dialogHeading}>{template.name}</h3>
          <p className={styles.dialogDescription}>
            Pré-visualização do modelo de certificado.
          </p>
        </div>
        <IconButton onClick={onClose} aria-label="Fechar">
          <CloseOutlined />
        </IconButton>
      </div>
      <div className={styles.previewDialogBody}>
        <div className={styles.certificateViewport}>
          <div
            className={`${styles.certificatePreview} ${
              isLandscape
                ? styles.certificateLandscape
                : styles.certificatePortrait
            }`}
            style={{
              backgroundImage,
              backgroundSize:
                page.background?.fit === "stretch"
                  ? "100% 100%"
                  : page.background?.fit || "contain",
            }}
          >
            {page.elements.map((element) => {
              const left = `${element.position.x || 0}px`;
              const top = `${element.position.y || 0}px`;
              const width = `${element.size.width || 100}px`;
              const height = `${element.size.height || 100}px`;

              if (element.type === "html_text") {
                return (
                  <div
                    key={element.id}
                    className={styles.previewText}
                    style={{
                      left,
                      top,
                      width,
                      color: element.style.color,
                      fontFamily: element.style.fontFamily,
                      fontSize: `${element.style.fontSize}px`,
                      textAlign: element.style.align,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: replaceCertificateVariables(element.contentHtml, variables),
                    }}
                  />
                );
              }

              if (element.type === "image") {
                return (
                  <img
                    key={element.id}
                    src={element.dataUrl}
                    alt={element.label}
                    className={styles.previewImage}
                    style={{ left, top, width, height }}
                  />
                );
              }

              return (
                <div
                  key={element.id}
                  className={
                    element.textPosition === "right"
                      ? styles.previewQrBlockRight
                      : styles.previewQrBlockBottom
                  }
                  style={{
                    left,
                    top,
                    width,
                    height,
                    color: element.style.color,
                    fontFamily: element.style.fontFamily,
                    fontSize: `${element.style.fontSize}px`,
                  }}
                >
                  {element.qrCodeDataUrl ? (
                    <img
                      src={element.qrCodeDataUrl}
                      alt={element.label}
                      style={{
                        width: `${element.qrSize || 100}px`,
                        height: `${element.qrSize || 100}px`,
                      }}
                    />
                  ) : null}
                  <div>
                    <div>
                      {replaceCertificateVariables(element.institutionText, variables)}
                    </div>
                    <div style={{ whiteSpace: "pre-line" }}>
                      {replaceCertificateVariables(element.instructionText, variables)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

type SurveyPreviewAnswer = string | number | string[];

const SurveyPreviewDialog = ({
  template,
  onClose,
}: {
  template: SatisfactionSurveyTemplate | null;
  onClose: () => void;
}) => {
  const [answers, setAnswers] = useState<Record<number, SurveyPreviewAnswer>>(
    {},
  );

  useEffect(() => {
    setAnswers({});
  }, [template?.id]);

  if (!template) return null;

  const questions = [...template.questions]
    .filter((question) => question.active)
    .sort((first, second) => first.ordem - second.ordem);

  const updateAnswer = (questionId: number, value: SurveyPreviewAnswer) => {
    setAnswers((current) => ({ ...current, [questionId]: value }));
  };

  const toggleCheckboxOption = (
    questionId: number,
    value: string,
    checked: boolean,
  ) => {
    const currentValues = Array.isArray(answers[questionId])
      ? (answers[questionId] as string[])
      : [];
    updateAnswer(
      questionId,
      checked
        ? [...currentValues, value]
        : currentValues.filter((currentValue) => currentValue !== value),
    );
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <div className={styles.dialogTitle}>
        <div>
          <h3 className={styles.dialogHeading}>{template.titulo}</h3>
          <p className={styles.dialogDescription}>
            {template.descricao || "Pesquisa de satisfação"}
          </p>
        </div>
        <IconButton onClick={onClose} aria-label="Fechar">
          <CloseOutlined />
        </IconButton>
      </div>
      <div className={styles.previewDialogBody}>
        <p className={styles.selectorText}>
          Pré-visualização com {questions.length} perguntas. As respostas
          informadas aqui não serão salvas.
        </p>
        <div className={styles.surveyQuestions}>
          {questions.map((question, index) => {
            const questionId = question.id || question.ordem;
            return (
              <article
                key={questionId}
                className={styles.surveyPreviewQuestion}
              >
                <div className={styles.surveyQuestionHeader}>
                  <p className={styles.surveyQuestionTitle}>
                    {index + 1}. {question.nome}
                  </p>
                  {question.obrigatorio ? (
                    <span className={styles.requiredBadge}>Obrigatória</span>
                  ) : null}
                </div>
                <SurveyPreviewField
                  question={question}
                  answer={answers[questionId]}
                  onChange={(value) => updateAnswer(questionId, value)}
                  onCheckboxChange={(value, checked) =>
                    toggleCheckboxOption(questionId, value, checked)
                  }
                />
              </article>
            );
          })}
          {questions.length === 0 ? (
            <p className={styles.emptyState}>
              Este modelo não possui perguntas ativas.
            </p>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
};

const SurveyPreviewField = ({
  question,
  answer,
  onChange,
  onCheckboxChange,
}: {
  question: SatisfactionSurveyQuestion;
  answer: SurveyPreviewAnswer | undefined;
  onChange: (value: SurveyPreviewAnswer) => void;
  onCheckboxChange: (value: string, checked: boolean) => void;
}) => {
  if (question.tipo === "texto_livre") {
    return (
      <TextField
        fullWidth
        multiline
        minRows={3}
        size="small"
        placeholder="Digite sua resposta"
        value={typeof answer === "string" ? answer : ""}
        onChange={(event) => onChange(event.target.value)}
        InputProps={{ className: stylesInput.inputSoft }}
      />
    );
  }

  if (question.tipo === "escala") {
    return (
      <RadioGroup
        value={answer ?? ""}
        onChange={(event) => onChange(Number(event.target.value))}
        className={styles.surveyScaleOptions}
      >
        {satisfactionScaleOptions.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" />}
            label={`${option.value} - ${option.label}`}
          />
        ))}
      </RadioGroup>
    );
  }

  if (question.tipo === "escala_dez") {
    return (
      <div className={styles.surveyScaleTen}>
        {Array.from({ length: 10 }, (_, index) => index + 1).map((value) => (
          <button
            key={value}
            type="button"
            className={`${styles.scaleTenButton} ${answer === value ? styles.scaleTenButtonActive : ""}`}
            onClick={() => onChange(value)}
          >
            {value}
          </button>
        ))}
      </div>
    );
  }

  if (question.tipo === "checkbox") {
    const values = Array.isArray(answer) ? answer : [];
    return (
      <div className={styles.surveyOptionGroup}>
        {(question.opcoes || []).map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                size="small"
                checked={values.includes(option.value)}
                onChange={(event) =>
                  onCheckboxChange(option.value, event.target.checked)
                }
              />
            }
            label={option.label}
          />
        ))}
      </div>
    );
  }

  if (question.tipo === "radio") {
    return (
      <RadioGroup
        value={typeof answer === "string" ? answer : ""}
        onChange={(event) => onChange(event.target.value)}
        className={styles.surveyOptionGroup}
      >
        {(question.opcoes || []).map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" />}
            label={option.label}
          />
        ))}
      </RadioGroup>
    );
  }

  const options = question.opcoes || [];
  return (
    <FormControl fullWidth size="small">
      <InputLabel
        id={`survey-preview-question-${question.id || question.ordem}`}
      >
        Selecione uma opção
      </InputLabel>
      <Select
        labelId={`survey-preview-question-${question.id || question.ordem}`}
        label="Selecione uma opção"
        value={typeof answer === "string" ? answer : ""}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.length > 0 ? (
          options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled value="">
            Opções carregadas durante o preenchimento
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

const LoadingState = () => (
  <div className={styles.container}>
    <div className={styles.section}>
      <Skeleton height={30} width="28%" />
      <Skeleton height={185} />
    </div>
    <div className={styles.section}>
      <Skeleton height={30} width="34%" />
      <Skeleton height={185} />
    </div>
  </div>
);

export default TurmaCertificadoPesquisaForm;
